import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Star, ChevronDown, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ManageTemplatesSheet } from './manage-templates-sheet';
import { ProjectForm } from './project-form';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { createProject, type CreateProjectData } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { toast } from 'sonner';
import { useTemplates, type ProjectTemplate } from '@/contexts/TemplateContext';

type WorkflowStyle = 'killer' | 'legacy';

interface ProjectCreationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit' | 'clone';
  projectId?: string;
  workflowStyle?: WorkflowStyle;
}

export function ProjectCreationModal({
  open,
  onOpenChange,
  mode = 'create',
  projectId,
  workflowStyle = 'killer',
  onProjectCreated,
}: ProjectCreationModalProps & { onProjectCreated?: () => void }) {
  const { formState, minimizeForm, restoreForm, clearForm } = useProjectForm();
  const { isDemoMode } = useDataMode();
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { templates, toggleFavorite: toggleTemplateFavorite } = useTemplates();
  const [isSaving, setIsSaving] = useState(false);
  const [currentFormData, setCurrentFormData] = useState<any>(null);
  const isKillerWorkflow = workflowStyle === 'killer';
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [aiMode, setAiMode] = useState(false);
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // For Killer UX, show form immediately; for Legacy, require mode selection first
  const [showForm, setShowForm] = useState(isKillerWorkflow);
  const [formProgress, setFormProgress] = useState(0);
  const [incompleteFields, setIncompleteFields] = useState<string[]>([]);
  // Killer UX: Track whether to show full details or just Quick Create
  const [showFullDetails, setShowFullDetails] = useState(false);
  // Track which field to highlight
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  // Track dropdown open state
  const [dropdownOpen, setDropdownOpen] = useState(false);


  // AI mode form state
  const [projectDescription, setProjectDescription] = useState('');
  const [projectObjectives, setProjectObjectives] = useState('');
  const [timeline, setTimeline] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [budget, setBudget] = useState('');
  const [deliverables, setDeliverables] = useState('');
  const [dependencies, setDependencies] = useState('');

  // Restore form if reopening with minimised state, or initialise for Killer UX
  useEffect(() => {
    if (open) {
      if (formState && !formState.isMinimized && formState.hasUnsavedChanges) {
        // Form was just restored (isMinimized is now false but has unsaved changes)
        setShowForm(true);
      } else if (isKillerWorkflow) {
        // For Killer UX, always show form immediately
        setShowForm(true);
      }
    } else {
      // Reset showForm when modal closes (for Legacy workflow)
      if (!isKillerWorkflow) {
        setShowForm(false);
      }
    }
  }, [open, formState, isKillerWorkflow]);

  const handleContinue = () => {
    // Handle continue logic based on mode (blank, template, or AI)
    if (aiMode) {
      // TODO: Process AI creation
      console.log('AI Mode:', { projectDescription, projectObjectives });
    } else {
      // Show the form
      setShowForm(true);
    }
  };

  const handleCancel = () => {
    // Reset state and close
    setUseTemplate(false);
    setSelectedTemplate('');
    setAiMode(false);
    setShowForm(isKillerWorkflow); // Keep form shown for Killer UX
    setShowFullDetails(false);
    setProjectDescription('');
    setProjectObjectives('');
    setTimeline('');
    setTeamSize('');
    setBudget('');
    setDeliverables('');
    setDependencies('');
    onOpenChange(false);
  };

  const handleFormSubmit= async (data: unknown) => {
    const formData = data as {
      projectName: string;
      projectCode?: string;
      description?: string;
      status: string;
      priority?: string;
      type: string;
      location?: string;
      startDate: Date;
      endDate: Date;
      projectOwner?: string;
      projectManager: string;
      projectManagers?: string[];
      clients?: string[];
      currencyCode?:string;
      budget?: string;
      tags?: string[];
    };

    setIsSaving(true);
    
    try {
      const projectData: CreateProjectData = {
        name: formData.projectName,
        projectCode: formData.projectCode,
        description: formData.description,
        statusId: Number(formData.status) as 1 | 2 | 3 | 4 | 5 | 6 | 7 || 1,
        priority: formData.priority,
        type: formData.type,
        location: formData.location,
        startDate: formData.startDate,
        endDate: formData.endDate,
        projectOwner: formData.projectOwner,
        projectManager: formData.projectManager,
        projectManagers: formData.projectManagers,
        clients: formData.clients,
        currencyCode:formData.currencyCode,
        budget: formData.budget,
        tags: formData.tags,
      };

      await createProject(projectData);
      
      if (isDemoMode) {
        toast.success('Project created successfully (Demo Mode - changes are not persisted)');
      } else {
        toast.success('Project created successfully');
      }
      
      clearForm();
      onOpenChange(false);
      onProjectCreated?.();
    } catch (error) {
      handleApiError(error, '/api/projects', 'POST');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormCancel = () => {
    handleCancel();
    clearForm();
  };

  const handleSaveAsDraft = (formData?: unknown) => {
    // Save form data as draft
    const dataToSave = formData || currentFormData;
    if (dataToSave) {
      minimizeForm(dataToSave);
    }
    onOpenChange(false);
  };

  const handleFieldClick= (fieldLabel: string) => {
    console.log('handleFieldClick called with:', fieldLabel);
    
    // Close the dropdown first
    setDropdownOpen(false);
    console.log('Dropdown closed');
    
    // Map field label back to field key for scrolling
    const fieldKeyMap: Record<string, string> = {
      'Project Name': 'projectName',
      'Project Code': 'projectCode',
      'Status': 'status',
      'Priority': 'priority',
      'Type': 'type',
      'Start Date': 'startDate',
      'End Date': 'endDate',
      'Project Manager': 'projectManager',
      'Description': 'description',
      'Budget': 'budget',
      'Clients': 'clients',
      'Tags': 'tags',
    };

    const fieldKey = fieldKeyMap[fieldLabel.replace(' *', '')];
    console.log('Field key mapped to:', fieldKey);
    
    if (fieldKey) {
      // Set highlighted field
      setHighlightedField(fieldKey);
      console.log('Highlighted field set to:', fieldKey);
      
      // Clear highlight after 1.5 seconds
      setTimeout(() => {
        setHighlightedField(null);
        console.log('Highlight cleared');
      }, 1500);
    } else {
      console.log('No field key found for label:', fieldLabel);
    }
  };

  const favoriteTemplates = templates.filter(t => t.isFavorite);
  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.templateDescription || t.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getModalTitle = () => {
    if (mode === 'edit') return 'Edit Project';
    if (mode === 'clone') return 'Clone Project';
    return aiMode ? 'AI Project Creation' : 'Create New Project';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideClose={true}
          className={cn(
            'max-w-4xl transition-all duration-300',
            (useTemplate && !aiMode) || showForm ? 'max-h-[90vh] overflow-hidden' : '',
            aiMode && !showForm && 'max-h-[90vh] overflow-y-auto'
          )}
        >
          <DialogHeader className="relative">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <DialogTitle>{showForm ? 'Create New Project' : getModalTitle()}</DialogTitle>
                <DialogDescription>
                  {showForm
                    ? 'Fill in the project details below. All fields marked with * are required.'
                    : aiMode
                    ? 'Describe your project and let AI create a comprehensive project plan for you.'
                    : mode === 'edit'
                    ? 'Update your project details and settings.'
                    : mode === 'clone'
                    ? 'Create a new project based on an existing one.'
                    : 'Choose how you want to start your new project.'}
                </DialogDescription>
              </div>
              {showForm && (
                <div className="flex items-center gap-4 shrink-0">
                  <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <button className="w-48 space-y-1 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md p-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground flex items-center gap-1">
                            Form Completion
                            {incompleteFields.length > 0 && (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </span>
                          <span className={cn(
                            "font-semibold",
                            formProgress === 100 ? "text-success-600 dark:text-success-400" : "text-muted-foreground"
                          )}>
                            {formProgress}%
                          </span>
                        </div>
                        <Progress 
                          value={formProgress} 
                          className={cn(
                            "h-1.5 transition-all",
                            formProgress === 100 && "bg-success-100 dark:bg-success-950"
                          )}
                        />
                      </button>
                    </DropdownMenuTrigger>
                    {incompleteFields.length > 0 && (
                      <DropdownMenuContent align="end" className="w-64 p-0">
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b">
                          Incomplete Required Fields ({incompleteFields.length})
                        </div>
                        <ScrollArea className="h-[132px]">
                          <ScrollAreaViewport className="h-full max-h-[132px]">
                            <div className="p-1">
                              {incompleteFields.map((field, index) => (
                                <DropdownMenuItem 
                                  key={index}
                                  className="flex items-start gap-2 py-2 cursor-pointer focus:bg-accent hover:bg-accent"
                                  onClick={() => handleFieldClick(field)}
                                >
                                  <AlertCircle className="h-4 w-4 text-warning-500 shrink-0 mt-0.5" />
                                  <span className="text-sm">{field}</span>
                                </DropdownMenuItem>
                              ))}
                            </div>
                          </ScrollAreaViewport>
                          <ScrollAreaScrollbar orientation="vertical" />
                        </ScrollArea>
                      </DropdownMenuContent>
                    )}
                  </DropdownMenu>
                </div>
              )}
            </div>
          </DialogHeader>

          <div className={cn(
            "py-4",
            showForm && "h-[calc(90vh-200px)]"
          )}>
            {showForm ? (
              <ProjectForm
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                onMinimize={handleSaveAsDraft}
                initialData={formState?.formData}
                onProgressChange={setFormProgress}
                onIncompleteFieldsChange={setIncompleteFields}
                onFormDataChange={setCurrentFormData}
                quickCreateMode={isKillerWorkflow}
                showFullDetails={showFullDetails}
                onShowFullDetails={() => setShowFullDetails(true)}
                onHideFullDetails={() => setShowFullDetails(false)}
                templates={templates}
                onTemplateSelect={(templateId) => {
                  if (templateId === 'none') {
                    setSelectedTemplate('');
                  } else {
                    setSelectedTemplate(templateId);
                    // TODO: Pre-fill form with template data when backend is ready
                    console.log('Template selected:', templateId);
                  }
                }}
                selectedTemplate={selectedTemplate}
                onAiAssist={() => {
                  // Show AI mode UI inline
                  setAiMode(true);
                  setShowFullDetails(true);
                }}
                highlightedField={highlightedField}
                onManageTemplates={() => setManageTemplatesOpen(true)}
              />
            ) : !aiMode && (
              <>
                {/* ========================================
                    LEGACY WORKFLOW - COMMENTED OUT
                    ========================================
                    This is the old multi-step workflow that required mode selection
                    before showing the form. Preserved for potential future use.
                    To re-enable: uncomment this section and update workflowStyle prop
                    ======================================== */}
                
                {/* 
                {/* AI Button - Prominent placement *\/}
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setAiMode(true)}
                    className="gap-2 border-primary/50 hover:border-primary hover:bg-primary/5"
                  >
                    <Sparkles className="h-5 w-5 text-primary" />
                    Create with AI
                  </Button>
                </div>

                {/* Divider *\/}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or</span>
                  </div>
                </div>

                {/* Project Type Toggle *\/}
                <div className="rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Blank Project Option *\/}
                    <div className={cn(
                      "flex-1 space-y-1 transition-opacity duration-200",
                      useTemplate && "opacity-40"
                    )}>
                      <Label 
                        htmlFor="use-template" 
                        className={cn(
                          "text-base font-semibold transition-colors cursor-pointer",
                          !useTemplate && "text-foreground",
                          useTemplate && "text-muted-foreground"
                        )}
                      >
                        Blank Project
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Start from scratch with an empty project
                      </p>
                    </div>

                    {/* Switch *\/}
                    <Switch
                      id="use-template"
                      checked={useTemplate}
                      onCheckedChange={setUseTemplate}
                      aria-label="Toggle between blank project and template"
                    />

                    {/* Template Project Option *\/}
                    <div className={cn(
                      "flex-1 space-y-1 transition-opacity duration-200",
                      !useTemplate && "opacity-40"
                    )}>
                      <Label 
                        htmlFor="use-template" 
                        className={cn(
                          "text-base font-semibold transition-colors cursor-pointer",
                          useTemplate && "text-foreground",
                          !useTemplate && "text-muted-foreground"
                        )}
                      >
                        Template Project
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Start with a pre-configured template
                      </p>
                    </div>
                  </div>
                </div>

                {/* Template Selection Area - Conditional Display *\/}
                {useTemplate && (
                  <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                    {/* Favorite Templates - Quick Access Cards *\/}
                    {favoriteTemplates.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">Favorite Templates</Label>
                        <div className="grid grid-cols-1 gap-3">
                          {favoriteTemplates.slice(0, 3).map(template => (
                            <Card
                              key={template.id}
                              className={cn(
                                'cursor-pointer transition-all hover:border-primary hover:shadow-md',
                                selectedTemplate === template.id &&
                                  'border-primary bg-primary/5'
                              )}
                              onClick={() => setSelectedTemplate(template.id)}
                            >
                              <CardContent className="flex items-start gap-3 p-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTemplateFavorite(template.id);
                                  }}
                                  className="mt-0.5 text-warning-500 hover:text-warning-600 transition-colors"
                                >
                                  <Star className="h-4 w-4 fill-current" />
                                </button>
                                <div className="flex-1 space-y-1">
                                  <h4 className="text-sm font-semibold">{template.name}</h4>
                                  <p className="text-xs text-muted-foreground">
                                    {template.description}
                                  </p>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Template Dropdown Selector *\/}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="template-select" className="text-sm font-semibold">
                          All Templates
                        </Label>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => setManageTemplatesOpen(true)}
                          className="h-auto p-0 text-xs"
                        >
                          Manage Templates
                        </Button>
                      </div>

                      {/* Search Input *\/}
                      <Input
                        placeholder="Search templates..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-9"
                      />

                      {/* Template Select *\/}
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger id="template-select" className="w-full">
                          <SelectValue placeholder="Select a template" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredTemplates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              <div className="flex items-center gap-2">
                                {template.isFavorite && (
                                  <Star className="h-3 w-3 fill-warning-500 text-warning-500" />
                                )}
                                <span>{template.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                */}
                
                {/* ======================================== */}
              </>
            )}

            {/* AI Mode Form */}
            {aiMode && (
              <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                {/* Back Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAiMode(false)}
                  className="mb-2"
                >
                  ← Back to standard creation
                </Button>

                {/* Primary Input - Project Description */}
                <div className="space-y-2">
                  <Label htmlFor="project-description" className="text-base font-semibold">
                    Project Description *
                  </Label>
                  <Textarea
                    id="project-description"
                    placeholder="Provide a concise overview of your project. For example: 'Implement a new customer portal with user authentication, dashboard, and reporting features. Target completion in 6 months with a team of 5 developers.'"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    className="min-h-24 resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Include what you're building, key features, and any important constraints
                  </p>
                </div>

                {/* Additional AI Inputs */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="objectives">Project Objectives</Label>
                    <Textarea
                      id="objectives"
                      placeholder="e.g., Increase customer satisfaction, reduce support costs..."
                      value={projectObjectives}
                      onChange={(e) => setProjectObjectives(e.target.value)}
                      className="min-h-20 resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeline">Timeline Expectations</Label>
                    <Input
                      id="timeline"
                      placeholder="e.g., 6 months, Q2 2024..."
                      value={timeline}
                      onChange={(e) => setTimeline(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="team-size">Team Size / Roles</Label>
                    <Input
                      id="team-size"
                      placeholder="e.g., 5 developers, 2 designers, 1 PM..."
                      value={teamSize}
                      onChange={(e) => setTeamSize(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget Constraints (Optional)</Label>
                    <Input
                      id="budget"
                      placeholder="e.g., $100,000, 1000 hours..."
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deliverables">Key Deliverables</Label>
                  <Textarea
                    id="deliverables"
                    placeholder="e.g., Mobile app, API documentation, user training materials..."
                    value={deliverables}
                    onChange={(e) => setDeliverables(e.target.value)}
                    className="min-h-20 resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dependencies">Dependencies / Integrations</Label>
                  <Textarea
                    id="dependencies"
                    placeholder="e.g., Integration with Salesforce, requires approval from legal..."
                    value={dependencies}
                    onChange={(e) => setDependencies(e.target.value)}
                    className="min-h-20 resize-none"
                  />
                </div>

                {/* AI Guidance Note */}
                <div className="rounded-lg bg-primary/5 p-4 border border-primary/20">
                  <div className="flex gap-2">
                    <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium">AI will generate:</p>
                      <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Project structure and phases</li>
                        <li>Task breakdown and dependencies</li>
                        <li>Resource allocation recommendations</li>
                        <li>Timeline and milestone suggestions</li>
                        <li>Risk assessment and mitigation strategies</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* LEGACY WORKFLOW FOOTER - Commented out as part of legacy workflow removal */}
          {/* 
          {!showForm && (
            <DialogFooter className="flex-row gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleCancel} className="flex-1 sm:flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleContinue}
                className="flex-1 sm:flex-1"
                disabled={aiMode && !projectDescription.trim()}
              >
                Continue
              </Button>
            </DialogFooter>
          )}
          */}
        </DialogContent>
      </Dialog>

      {/* Manage Templates Side-Drawer */}
      <ManageTemplatesSheet
        open={manageTemplatesOpen}
        onOpenChange={setManageTemplatesOpen}
      />
    </>
  );
}
